import { Container, Group, Stack, TextInput } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { IconSearch } from "@tabler/icons-react"
import type { CountryIso3166 } from "@tfg/db"
import {
  CountrySelector,
  ExportButton,
  ExportType,
  InfoBox,
  Select,
} from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  orderByValidationType,
  supplierMonitoringFiltersValidationType,
  SupplierTypeValidationType,
} from "@tfg/types/schemas/organization/schemas"

import { DataFetchWrapper } from "~/components/DataFetchWrapper"
import { HeaderControlsProvider } from "~/components/contexts/useHeaderControlsContext"
import { usePagination } from "~/components/hooks/usePagination"
import { i18n } from "~/i18n.ts"
import { SupplierMonitoringList } from "~/pages/Brand/Suppliers/List/SupplierMonitoring.tsx"
import {
  useListSuppliersCountriesQuery,
  useListSuppliersQuery,
} from "~/pages/Brand/Suppliers/List/supplierHooks.ts"
import { SupplierSort } from "~/pages/Brand/Suppliers/SupplierSort"
import { useDownloadSuppliersCSVMutation } from "~/pages/Brand/Suppliers/hooks.ts"
import { useListSubSuppliersQuery } from "~/pages/SupplierPortal/SubSupplierMonitoring/hooks/useListSubSuppliersQuery.ts"
import { COUNTRIES } from "~/utils/countries.ts"

export const SUPPLIER_DIRECT_OPTIONS = [
  { label: i18n.t("suppliers:filter.all"), value: "all" },
  { label: i18n.t("suppliers:filter.direct"), value: "direct" },
  {
    label: i18n.t("suppliers:filter.nonConfidential"),
    value: "indirect",
  },
  {
    label: i18n.t("suppliers:filter.indirectConfidential"),
    value: "indirectConfidential",
  },
]

const hooks = {
  supplier: {
    getList: useListSuppliersQuery,
    countries: useListSuppliersCountriesQuery,
  },
  subSupplier: {
    getList: useListSubSuppliersQuery,
    countries: () => ({ data: undefined }),
  },
}

type SupplierMonitoringWrapperProps = {
  type: "supplier" | "subSupplier"
}

const SupplierMonitoring = ({ type }: SupplierMonitoringWrapperProps) => {
  const { t } = useTranslation(["suppliers", "common"])
  const [sort, setSort] = useState<Partial<orderByValidationType>>({})
  const {
    cursorBasedPagination,
    pageBasedPagination,
    setPageBasedPagination,
    goToFirstPage,
  } = usePagination()
  const [searchSupplier, setSearchSupplier] = useState<string>("")
  const [debouncedSupplier] = useDebouncedValue<string>(searchSupplier, 600)

  const suppliersCountriesQuery = hooks[type].countries()
  const suppliersCountries = suppliersCountriesQuery.data ?? undefined

  const [filtersValue, setFiltersValue] = useState<
    Partial<supplierMonitoringFiltersValidationType>
  >({})

  const suppliersQuery = hooks[type].getList({
    fetchDataOptions: cursorBasedPagination,
    searchSupplier: debouncedSupplier,
    filters: filtersValue,
    orderBy: sort,
  })
  useEffect(() => {
    goToFirstPage()
  }, [debouncedSupplier, filtersValue, goToFirstPage])

  const { mutate: downloadMutation, isPending: isSuppliersCsvDownloading } =
    useDownloadSuppliersCSVMutation({
      searchSupplier,
      filters: filtersValue,
      orderBy: sort,
    })
  return (
    <Stack>
      <Group justify="space-between">
        <Group align="flex-end" gap="xs">
          <TextInput
            value={searchSupplier}
            autoFocus
            onChange={(e) => setSearchSupplier(e.target.value)}
            w={180}
            leftSection={<IconSearch size={16} />}
            placeholder={t("list.searchPlaceholder")}
          />
          <SupplierSort sort={sort} setSort={setSort} />
          <Select
            data={SUPPLIER_DIRECT_OPTIONS}
            placeholder={t("list.supplierTypePlaceholder")}
            clearable
            value={filtersValue.supplierType}
            onChange={(value) => {
              setFiltersValue({
                ...filtersValue,
                supplierType:
                  (value as SupplierTypeValidationType) ?? undefined,
              })
            }}
          />
          <CountrySelector
            value={filtersValue.country ?? null}
            countriesMapper={COUNTRIES}
            onChange={(value) =>
              setFiltersValue({
                ...filtersValue,
                country: (value as CountryIso3166) ?? undefined,
              })
            }
            selectCountries={suppliersCountries}
            searchable
            clearable
            placeholder={t("filter.country")}
          />
        </Group>
        {type === "supplier" && (
          <ExportButton<void>
            label={t("common:actionButtons.export")}
            exportOptions={[
              {
                type: ExportType.CSV,
                label: t("common:actionButtons.exportAsCsv"),
                exportFunction: downloadMutation,
                isLoading: isSuppliersCsvDownloading,
              },
            ]}
          />
        )}
      </Group>
      <DataFetchWrapper query={suppliersQuery}>
        {({ data }) =>
          data.suppliers.length > 0 ? (
            <SupplierMonitoringList
              suppliers={data.suppliers}
              count={data.count}
              setPagination={setPageBasedPagination}
              pagination={pageBasedPagination}
              searchSupplier={searchSupplier}
              setSearchSupplier={setSearchSupplier}
              setFiltersValue={setFiltersValue}
              filtersValue={filtersValue}
              type={type}
            />
          ) : (
            <Container mt="10%">
              <InfoBox message={t("list.noSuppliers")} />
            </Container>
          )
        }
      </DataFetchWrapper>
    </Stack>
  )
}

export const SupplierMonitoringWrapper = ({
  type,
}: SupplierMonitoringWrapperProps) => {
  return (
    <HeaderControlsProvider>
      <SupplierMonitoring type={type} />
    </HeaderControlsProvider>
  )
}
